/**
 * THE formatting module (§11 number/date/currency contract). Every money, date, phone and percent on
 * every surface — screen and paper — goes through here. Absent or non-finite → an em dash, never NaN,
 * never $0.00, never blank. A date-only string is parsed as LOCAL midnight (UTC parsing shows a wrong
 * arrival date to everyone west of Greenwich).
 */
export const DASH = '—';

const moneyFmt = new Map<string, Intl.NumberFormat>();
export function money(cents: number | null | undefined, currency = 'USD'): string {
  if (cents == null || !Number.isFinite(cents)) return DASH;
  let f = moneyFmt.get(currency);
  if (!f) {
    f = new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
    moneyFmt.set(currency, f);
  }
  return f.format(cents / 100);
}

/** Parse 'YYYY-MM-DD' as local midnight; full timestamps as instants. */
export function parseDate(v: string | Date | null | undefined): Date | null {
  if (v == null || v === '') return null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v : null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
  return Number.isFinite(d.getTime()) ? d : null;
}

const dateFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const dateTimeFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const monthDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });

export function date(v: string | Date | null | undefined): string {
  const d = parseDate(v);
  return d ? dateFmt.format(d) : DASH;
}
/** `Nov 13`: a day with no year, for a place too narrow for one (a day tile). */
export function shortDate(v: string | Date | null | undefined): string {
  const d = parseDate(v);
  return d ? monthDay.format(d) : DASH;
}
const monthYearFmt = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });
/** `November 2031`: the month a day is in, for the head of a calendar. */
export function monthYear(v: string | Date | null | undefined): string {
  const d = parseDate(v);
  return d ? monthYearFmt.format(d) : DASH;
}
const weekdayFmt = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
/** `Thu`: the day of the week, short. */
export function weekday(v: string | Date | null | undefined): string {
  const d = parseDate(v);
  return d ? weekdayFmt.format(d) : DASH;
}
export function dateTime(v: string | Date | null | undefined): string {
  const d = parseDate(v);
  return d ? dateTimeFmt.format(d).replace(' at ', ', ') : DASH;
}
const zonedFmt = new Map<string, Intl.DateTimeFormat>();
/**
 * A moment on the clock of a named time zone, with the zone's short name: `Nov 13, 2026, 9:00 AM EST`. For a time that
 * belongs to a place (when an event's booking opens), so every reader sees the same words wherever they are. A zone
 * that is blank or unknown reads as UTC.
 */
export function dateTimeIn(v: string | Date | null | undefined, timeZone: string | null | undefined): string {
  const d = parseDate(v);
  if (!d) return DASH;
  const make = (zone: string) => new Intl.DateTimeFormat('en-US', { timeZone: zone, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  const zone = (timeZone ?? '').trim() || 'UTC';
  let f = zonedFmt.get(zone);
  if (!f) {
    try { f = make(zone); } catch { f = make('UTC'); }
    zonedFmt.set(zone, f);
  }
  return f.format(d).replace(' at ', ', ');
}
export function time(v: string | Date | null | undefined): string {
  const d = parseDate(v);
  return d ? timeFmt.format(d) : DASH;
}

const timeSecFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' });
/** A clock time with its seconds ("3:21:04 PM") — for "no update since", where a minute is too coarse. */
export function timeSeconds(v: string | Date | number | null | undefined): string {
  const d = typeof v === 'number' ? (Number.isFinite(v) ? new Date(v) : null) : parseDate(v);
  return d ? timeSecFmt.format(d) : DASH;
}
/** How long ago, from a count of seconds: "just now" under 5 s, then "12 s ago", "4 min ago", "2 h ago". */
export function ago(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return DASH;
  const s = Math.max(0, Math.floor(seconds));
  if (s < 5) return 'just now';
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  return `${Math.floor(s / 3600)} h ago`;
}

/** `Apr 5 – 8, 2026` · `Apr 5 – May 8, 2026` · `Apr 5, 2026 – Jan 8, 2027` (en dash with spaces). */
export function dateRange(start: string | Date | null | undefined, end: string | Date | null | undefined): string {
  const a = parseDate(start);
  const b = parseDate(end);
  if (!a && !b) return 'Dates not set';
  if (!a || !b) return dateFmt.format((a ?? b)!);
  if (a.getFullYear() !== b.getFullYear()) return `${dateFmt.format(a)} – ${dateFmt.format(b)}`;
  if (a.getMonth() !== b.getMonth()) return `${monthDay.format(a)} – ${monthDay.format(b)}, ${b.getFullYear()}`;
  if (a.getDate() === b.getDate()) return dateFmt.format(a);
  return `${monthDay.format(a)} – ${b.getDate()}, ${b.getFullYear()}`;
}

export function percent(v: number | null | undefined, decimals = 0): string {
  if (v == null || !Number.isFinite(v)) return DASH;
  return `${v.toFixed(decimals)}%`;
}

/** A number with a fixed count of decimals (scores, feet, points) — the one place a decimal is rendered. */
export function decimal(n: number | null | undefined, digits = 1): string {
  if (n == null || !Number.isFinite(n)) return DASH;
  return n.toFixed(digits);
}

export function count(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return DASH;
  return new Intl.NumberFormat('en-US').format(n);
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${count(n)} ${n === 1 ? one : many}`;
}

export function bytes(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return DASH;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** US numbers render `+1 (555) 123-4567`; anything else is shown as entered. */
export function phone(v: string | null | undefined): string {
  if (!v) return DASH;
  const digits = v.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return d.length === 10 ? `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : v;
}

/** Customer-grade file names (owner §2.10): `Ashi Demo Weekend – Rooming List – The Ashi.pdf`. */
export function fileName(parts: Array<string | null | undefined>, ext: string): string {
  const safe = parts.filter(Boolean).map((p) => String(p).replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim());
  return `${safe.join(' – ')}.${ext}`;
}

export function floorLabel(floor: number, names?: Record<string, string> | null): string {
  return names?.[String(floor)] || `Floor ${floor}`;
}

/** A wall-clock "HH:MM[:SS]" (a hotel's check-in time) as "3:00 PM"; passes anything else through. */
export function clockTime(v: string | null | undefined): string {
  if (!v) return '';
  const m = /^(\d{1,2}):(\d{2})/.exec(v);
  if (!m) return v;
  const h = Number(m[1]);
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}
